<script>
import { mapState } from "vuex";
import { api } from "@/state/services";
import {
  carregarConfigMetas,
  chaveUsuario,
  isSecretaria,
  temApvs,
  sugestao,
  avisoQuantidade,
  fmtMoeda,
  plural,
  nomeMes,
} from "@/utils/minhas-metas";
import { gravarVistaLocal } from "@/utils/jornada";

// "Agora não": o wizard automático some por alguns dias naquele dispositivo. Não é
// "nunca mais" — quem não planejou volta a ver o convite, e o painel sempre oferece o botão.
const ADIADO_PREFIX = "metas_onboarding_adiado:";
const ADIADO_DIAS = 3;
// Chave do tour de boas-vindas (ver onboarding-modal.vue) — usada só para saber se ele acabou.
const TOUR_LOCAL_PREFIX = "onboarding_done:";
// Faixa do valor do rastreador cobrado na adesão (Brasil e Truck), para o aviso da prévia.
// A média usada no cálculo vem de `premissas[produto].rastreador` (Brasil 50, Truck 150).
const RASTREADOR_FAIXA = [50, 250];

/**
 * Onboarding de METAS (wizard de planejamento). NÃO é o tour do menu (onboarding-modal.vue).
 *
 * PRECEDÊNCIA (montado por último em router/layouts/main.vue):
 *   RegionalCpfModal -> AfiliadosRegulamentoModal -> OnboardingModal (tour) -> ESTE.
 *   Abre sozinho só quando: nenhum fluxo obrigatório em cena, o tour já foi concluído (flag no
 *   banco ou fallback local), não há replay do tour pedido, o usuário tem APVS e ainda não
 *   planejou. O botão "Refazer meu planejamento" do painel abre manualmente (account.metasWizard),
 *   respeitando os mesmos bloqueios.
 *
 * REVISÃO DO MÊS: quem já planejou antes deste mês recebe, no 1º acesso a partir do dia 1, o
 *   mesmo wizard só com os 2 últimos passos (quantidade + prévia). Quem decide é a API
 *   (config.revisao.pendente); ao abrir, POST metas/revisao marca o mês como visto (1x por mês,
 *   em qualquer aparelho). O "Refazer meu planejamento" continua com os 4 passos.
 *
 * SUGESTÃO: média de adesões dos últimos 3 meses + 20% (config.sugestao_media, pronta da API).
 *   Só substitui a meta já cadastrada (ou o recomendado, para quem nunca planejou) quando a média
 *   for MAIOR que ela; sem média, fica o que já estava.
 *
 * SECRETÁRIA: nunca abre (o planejamento é da titular; a API também recusa o PUT).
 *
 * b-modal controlado por :visible/@change (NÃO v-model/:value — o b-modal expõe `visible`).
 */
export default {
  name: "MetasOnboardingModal",
  data() {
    return {
      RASTREADOR_FAIXA,
      aberto: false,
      passo: 0,
      salvando: false,
      erro: "",
      salvo: false,
      aberturaManual: false,
      modoRevisao: false,
      // Revisão do mês já aberta nesta sessão (não reabre enquanto o cache da config não atualiza).
      revisaoAberta: false,
      // Sem o passo "Seu objetivo": o objetivo fica no padrão (ou no que já estava salvo) e só
      // serve de base para a sugestão de quantidade.
      form: {
        objetivo: "gordura",
        meta_brasil: 0,
        meta_truck: 0,
      },
      editado: { brasil: false, truck: false },
      adiadoAte: 0,
    };
  },
  computed: {
    ...mapState({ account: (state) => state.account }),
    user() {
      return (this.account && this.account.user) || null;
    },
    chave() {
      return chaveUsuario(this.user);
    },
    config() {
      const c = this.account && this.account.metasConfig;
      return c && c.key === this.chave ? c.data : null;
    },
    regras() {
      return (this.config && this.config.regras) || {};
    },
    // Premissa da prévia por produto (Truck: adesão maior e rastreador médio). Fallback = valores
    // atuais da API, caso ela ainda seja a versão antiga (premissa única).
    premissas() {
      const padrao = {
        brasil: { adesao_minima: 250, rastreador: 50, base_adesao: 200, rastreador_variacao: null },
        truck: { adesao_minima: 800, rastreador: 150, base_adesao: 650, rastreador_variacao: [50, 150, 250] },
      };
      const out = {};
      ["brasil", "truck"].forEach((p) => {
        out[p] = (this.regras[p] && this.regras[p].premissas) || padrao[p];
      });
      return out;
    },
    // Quadros do passo 1 por produto: Consultor, Regional Home Office e Regional Executiva.
    quadrosRegras() {
      return ["brasil", "truck"]
        .filter((p) => this.regras[p] && (p === "brasil" ? this.temBrasil || !this.temTruck : this.temTruck))
        .map((p) => {
          const r = this.regras[p];
          return {
            produto: p,
            nome: r.nome,
            soma: r.soma,
            consultor: r.meta_consultor,
            homeoffice: { meta: r.meta_homeoffice, faixas: this.textoFaixas(r.faixas_homeoffice) },
            executiva: { meta: r.meta_executiva, faixas: this.textoFaixas(r.faixas_executiva) },
          };
        })
        .filter((q) => q.homeoffice.meta && q.executiva.meta);
    },
    modalidade() {
      const r = this.regras.brasil || this.regras.truck || {};
      return r.modalidade || (this.isRegional ? "executiva" : "consultor");
    },
    perfil() {
      return (this.config && this.config.perfil) || { tipo: "consultor" };
    },
    isRegional() {
      return this.perfil.tipo === "regional";
    },
    temBrasil() {
      return !!(this.config && this.config.produtos && this.config.produtos.brasil);
    },
    temTruck() {
      return !!(this.config && this.config.produtos && this.config.produtos.truck);
    },
    // Não há mais a pergunta "Você também produz Truck?": quem tem Truck sempre vê o campo, e
    // quem tem Brasil + Truck e não vai produzir Truck deixa 0 (só Truck => produz por definição).
    produzTruck() {
      if (!this.temTruck) return false;
      if (!this.temBrasil) return true;
      return Number(this.form.meta_truck) > 0;
    },
    metaMax() {
      return (this.config && this.config.limites && this.config.limites.meta_max) || 500;
    },

    // Mesmos bloqueios do tour (onboarding-modal.vue::blockedByRequiredFlow).
    bloqueado() {
      const user = this.user;
      if (!user) return true;
      const path = (this.$route && this.$route.path) || "";
      if (
        path === "/atualizacad" ||
        path === "/documents" ||
        path.indexOf("/contracts/") === 0 ||
        path.indexOf("/login") === 0 ||
        path === "/logout"
      ) {
        return true;
      }
      if (user.atualizacad === "pending") return true;
      if (user.documents === "pending") return true;
      if (user.contracts && user.contracts.length > 0) return true;
      if (Number(user.regional) === 1 && !user.cpf_regional) return true;
      if (this.account && this.account.afiliadosRegulamentoPendingPath) return true;
      if (this.account && this.account.onboardingReplay) return true;
      return false;
    },
    tourConcluido() {
      const user = this.user;
      if (!user) return false;
      if (user.onboarding_done === "yes") return true;
      try {
        return localStorage.getItem(TOUR_LOCAL_PREFIX + user.id) === "yes";
      } catch (e) {
        return false;
      }
    },
    podeUsar() {
      return !!this.user && !isSecretaria(this.user) && temApvs(this.user) && !this.bloqueado;
    },
    elegivelAuto() {
      return this.podeUsar && this.tourConcluido && this.adiadoAte <= Date.now();
    },
    deveAbrirAuto() {
      return (
        this.elegivelAuto &&
        !!this.config &&
        this.config.disponivel === true &&
        !(this.config.planejamento && this.config.planejamento.configurado)
      );
    },
    deveAbrirRevisao() {
      return (
        this.elegivelAuto &&
        !this.revisaoAberta &&
        !!this.config &&
        this.config.disponivel === true &&
        !!(this.config.planejamento && this.config.planejamento.configurado) &&
        !!(this.config.revisao && this.config.revisao.pendente)
      );
    },
    pedidoManual() {
      return !!(this.account && this.account.metasWizard);
    },
    mesRevisao() {
      const r = this.config && this.config.revisao;
      return r && r.mes ? nomeMes(r.mes) : nomeMes(new Date().getMonth() + 1);
    },

    passos() {
      const todos = [
        { key: "regras", titulo: "Como a meta funciona" },
        { key: "boleto", titulo: "A regra do 1º boleto" },
        { key: "quantidade", titulo: "Quantas adesões por mês?" },
        { key: "previa", titulo: "Prévia do mês" },
      ];
      return this.modoRevisao ? todos.slice(2) : todos;
    },
    passoAtual() {
      return this.salvo ? { key: "pronto", titulo: "Planejamento salvo!" } : this.passos[this.passo];
    },
    ultimoPasso() {
      return this.passo === this.passos.length - 1;
    },
    // Produtos com campo de quantidade: todos os que o usuário tem.
    produtosPlano() {
      const lista = [];
      if (this.temBrasil) lista.push("brasil");
      if (this.temTruck) lista.push("truck");
      return lista;
    },
    avisos() {
      return {
        brasil: avisoQuantidade(this.form.meta_brasil, this.regras.brasil),
        truck: avisoQuantidade(this.form.meta_truck, this.regras.truck),
      };
    },
    previa() {
      // Truck com 0 (não produz) sai da prévia e da trajetória.
      const produtos = this.produtosPlano.filter((p) => p !== "truck" || this.produzTruck);
      const linhas = produtos.map((p) => {
        const qtd = Number(p === "brasil" ? this.form.meta_brasil : this.form.meta_truck) || 0;
        const base = Number(this.premissas[p].base_adesao) || 0;
        return { produto: p, nome: this.regras[p] ? this.regras[p].nome : p, qtd, base, valor: qtd * base };
      });
      const total = linhas.reduce((s, l) => s + l.valor, 0);
      return { linhas, total, anual: total * 12 };
    },
    podeAvancar() {
      const key = this.passoAtual && this.passoAtual.key;
      if (key === "quantidade") {
        return this.produtosPlano.every((p) => this.quantidadeValida(p === "brasil" ? this.form.meta_brasil : this.form.meta_truck));
      }
      return true;
    },
  },
  watch: {
    elegivelAuto: {
      immediate: true,
      handler(val) {
        if (val) this.garantirConfig();
      },
    },
    deveAbrirAuto: {
      immediate: true,
      handler(val) {
        if (val && !this.aberto) this.abrir(false);
      },
    },
    deveAbrirRevisao: {
      immediate: true,
      handler(val) {
        if (val && !this.aberto) this.abrirRevisao();
      },
    },
    pedidoManual: {
      immediate: true,
      handler(val) {
        if (!val) return;
        if (!this.podeUsar) {
          this.$store.commit("account/setMetasWizard", false);
          return;
        }
        // Sempre busca de novo: a média dos 3 meses e a meta salva podem ter mudado desde o login.
        this.garantirConfig(true).then(() => {
          if (this.config && this.config.disponivel) {
            this.abrir(true);
          } else {
            this.$store.commit("account/setMetasWizard", false);
          }
        });
      },
    },
    bloqueado(val) {
      if (val && this.aberto) {
        this.aberto = false;
        this.$store.commit("account/setMetasWizard", false);
      }
    },
    "form.objetivo"() {
      this.aplicarSugestoes();
    },
  },
  created() {
    this.adiadoAte = this.lerAdiado();
  },
  methods: {
    fmtMoeda,
    plural,
    // [{minimo, percentual}] da maior para a menor => "15 ou mais", "10 a 14", "2".
    textoFaixas(lista) {
      return (lista || []).map((f, i) => {
        let texto;
        if (i === 0) texto = f.minimo + " ou mais";
        else {
          const ate = lista[i - 1].minimo - 1;
          texto = ate > f.minimo ? f.minimo + " a " + ate : String(f.minimo);
        }
        return { texto, percentual: f.percentual };
      });
    },
    garantirConfig(force) {
      return carregarConfigMetas(this.$store, { force: !!force }).catch(() => {
        // Silencioso: o wizard é opcional; o painel mostra o erro com "tentar de novo".
      });
    },
    lerAdiado() {
      if (!this.chave) return 0;
      try {
        return Number(localStorage.getItem(ADIADO_PREFIX + this.chave)) || 0;
      } catch (e) {
        return 0;
      }
    },
    gravarAdiado() {
      if (!this.chave) return;
      const ate = Date.now() + ADIADO_DIAS * 24 * 60 * 60 * 1000;
      this.adiadoAte = ate;
      try {
        localStorage.setItem(ADIADO_PREFIX + this.chave, String(ate));
      } catch (e) {
        // sem storage: o adiamento vale só para esta sessão (adiadoAte em memória)
      }
    },
    limparAdiado() {
      this.adiadoAte = 0;
      try {
        localStorage.removeItem(ADIADO_PREFIX + this.chave);
      } catch (e) {
        // ignorado
      }
    },
    abrir(manual, revisao) {
      const plan = (this.config && this.config.planejamento) || {};
      this.aberturaManual = !!manual;
      this.modoRevisao = !!revisao;
      this.erro = "";
      this.salvo = false;
      this.salvando = false;
      this.passo = 0;
      this.editado = { brasil: false, truck: false };
      this.form = {
        objetivo: (plan.configurado && plan.objetivo) || "gordura",
        meta_brasil: this.sugestaoProduto("brasil"),
        meta_truck: this.sugestaoProduto("truck"),
      };
      this.aberto = true;
    },
    // Popup do mês: marca como visto já na abertura (1x por mês, em qualquer aparelho), mesmo que
    // a pessoa feche sem salvar.
    abrirRevisao() {
      this.revisaoAberta = true;
      this.abrir(false, true);
      api
        .post("metas/revisao")
        .then(() => {
          const atual = this.account.metasConfig;
          if (atual && atual.key === this.chave) {
            this.$store.commit("account/setMetasConfig", {
              key: this.chave,
              data: { ...atual.data, revisao: { ...(atual.data.revisao || {}), pendente: false } },
            });
          }
        })
        .catch(() => {
          // Sem registro: o popup volta no próximo acesso — melhor que perder a revisão do mês.
        });
    },
    // Meta já cadastrada do produto: a salva no planejamento ou, para quem nunca planejou, o
    // mínimo recomendado. Quem tem Brasil + Truck e salvou sem produzir Truck fica com 0.
    metaCadastrada(produto) {
      const plan = (this.config && this.config.planejamento) || {};
      const regras = this.regras[produto];
      if (plan.configurado) {
        if (produto === "truck" && this.temTruck && this.temBrasil && !plan.produz_truck) return 0;
        const salva = produto === "brasil" ? plan.meta_brasil : plan.meta_truck;
        if (salva != null) return salva;
      }
      return sugestao("gordura", regras);
    },
    mediaProduto(produto) {
      const s = this.config && this.config.sugestao_media;
      const m = s && s[produto];
      return m && m.media ? m : null;
    },
    // A API calculou e não houve adesão nos últimos meses (diferente de "não calculou").
    semMedia(produto) {
      const s = this.config && this.config.sugestao_media;
      const m = s && s[produto];
      return !!m && !m.media;
    },
    // Média dos 3 meses + 20% só quando a média é MAIOR que a meta já cadastrada.
    mediaVence(produto) {
      const m = this.mediaProduto(produto);
      return !!m && Number(m.media) > Number(this.metaCadastrada(produto));
    },
    sugestaoProduto(produto) {
      const atual = this.metaCadastrada(produto);
      if (!this.mediaVence(produto)) return atual;
      return Math.min(this.metaMax, Number(this.mediaProduto(produto).sugerido) || atual);
    },
    fmtMedia(valor) {
      return Number(valor).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
    },
    aplicarSugestoes() {
      if (!this.editado.brasil) this.form.meta_brasil = this.sugestaoProduto("brasil");
      if (!this.editado.truck) this.form.meta_truck = this.sugestaoProduto("truck");
    },
    usarSugestao(produto) {
      this.editado[produto] = false;
      this.aplicarSugestoes();
    },
    alterarQtd(produto, delta) {
      const campo = produto === "brasil" ? "meta_brasil" : "meta_truck";
      const atual = Number(this.form[campo]) || 0;
      this.form[campo] = Math.min(this.metaMax, Math.max(0, atual + delta));
      this.editado[produto] = true;
    },
    onInputQtd(produto) {
      this.editado[produto] = true;
    },
    quantidadeValida(v) {
      const n = Number(v);
      return v !== "" && v !== null && Number.isInteger(n) && n >= 0 && n <= this.metaMax;
    },
    porSemana(qtd) {
      return (Number(qtd) / 4.3).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
    },
    aCadaDias(qtd) {
      const n = Number(qtd);
      if (!n) return null;
      return Math.max(1, Math.floor(30 / n));
    },
    voltar() {
      if (this.passo > 0) this.passo--;
    },
    avancar() {
      if (!this.podeAvancar) return;
      if (this.ultimoPasso) {
        this.salvar();
        return;
      }
      this.passo++;
    },
    irPara(i) {
      if (i < this.passo) this.passo = i;
    },
    salvar() {
      if (this.salvando) return;
      this.salvando = true;
      this.erro = "";

      const payload = {
        produz_truck: this.produzTruck,
        objetivo: this.form.objetivo,
        meta_brasil: this.temBrasil ? Number(this.form.meta_brasil) : null,
        meta_truck: this.produzTruck ? Number(this.form.meta_truck) : null,
      };

      api
        .put("metas/config", payload)
        .then((response) => {
          const data = response && response.data;
          if (!data || data.status !== "success") {
            this.erro = (data && data.message) || "Não foi possível salvar agora. Tente novamente.";
            return;
          }
          const atual = this.account.metasConfig;
          if (atual && atual.key === this.chave) {
            this.$store.commit("account/setMetasConfig", {
              key: this.chave,
              data: { ...atual.data, planejamento: data.planejamento },
            });
          }
          this.limparAdiado();
          // Acabou de planejar: não emenda a Jornada diária em seguida neste aparelho hoje
          // (cache local apenas — o servidor não é marcado).
          gravarVistaLocal(this.user);
          this.salvo = true;
        })
        .catch(() => {
          this.erro = "Sem conexão com o servidor. Confira a sua internet e tente de novo.";
        })
        .finally(() => {
          this.salvando = false;
        });
    },
    // Fechar pelo X / "Agora não" / Esc.
    fechar() {
      if (!this.aberto) return;
      const planejado = this.config && this.config.planejamento && this.config.planejamento.configurado;
      if (!planejado && !this.salvo) this.gravarAdiado();
      this.aberto = false;
      this.$store.commit("account/setMetasWizard", false);
    },
    onChange(visivel) {
      if (!visivel) this.fechar();
    },
    verPainel() {
      this.aberto = false;
      this.$store.commit("account/setMetasWizard", false);
      if (this.$route.path !== "/minhas-metas") {
        this.$router.push("/minhas-metas").catch(() => {});
      }
    },
  },
};
</script>

<template>
  <b-modal
    :visible="aberto"
    size="lg"
    centered
    scrollable
    no-close-on-backdrop
    dialog-class="mm-wizard-dialog"
    content-class="mm-wizard-content"
    title-tag="h2"
    header-class="mm-wizard-header"
    @change="onChange"
  >
    <template #modal-title>
      <span class="mm-wizard-kicker">{{ modoRevisao ? "Planejamento de " + mesRevisao : "Planejamento de metas" }}</span>
      <span class="mm-wizard-title">{{ passoAtual ? passoAtual.titulo : "" }}</span>
    </template>

    <div v-if="config" class="mm-wizard">
      <ol v-if="!salvo" class="mm-steps" aria-label="Etapas">
        <li
          v-for="(p, i) in passos"
          :key="p.key"
          :class="{ ativo: i === passo, feito: i < passo }"
          :aria-current="i === passo ? 'step' : null"
        >
          <button type="button" :disabled="i >= passo" :aria-label="'Etapa ' + (i + 1) + ': ' + p.titulo" @click="irPara(i)">
            {{ i + 1 }}
          </button>
        </li>
      </ol>

      <!-- 1. Regras -->
      <section v-if="passoAtual.key === 'regras'">
        <p class="mm-lead">
          Em 2 minutos você define quantas adesões quer trazer por mês e passa a acompanhar tudo em
          <strong>Minhas Metas</strong>. Primeiro, como a meta funciona:
        </p>

        <div v-for="q in quadrosRegras" :key="q.produto" class="mm-produto-regras">
          <h3 class="mm-produto-titulo">Como a meta funciona no {{ q.nome }}</h3>
          <div class="mm-regra-grid mm-regra-grid-3">
            <div class="mm-regra" :class="{ destaque: modalidade === 'consultor' }">
              <h3>Consultor — meta {{ q.consultor }}</h3>
              <ul>
                <li>
                  Batendo <strong>{{ q.consultor }} sozinho</strong>, recebe
                  {{ regras[q.produto].percentual_consultor }}% dos boletos pagos da própria base <strong>e</strong> dos
                  boletos pagos dos consultores abaixo que não bateram a meta.
                </li>
                <li>
                  Também dá para bater <strong>em equipe</strong>: trazendo pelo menos 1 meta própria, as metas dos
                  consultores abaixo que não bateram sobem para você. Nesse caso você recebe
                  {{ regras[q.produto].percentual_consultor }}% <strong>somente da própria base</strong>.
                </li>
              </ul>
            </div>
            <div class="mm-regra" :class="{ destaque: modalidade === 'homeoffice' }">
              <h3>Regional Home Office — meta {{ q.homeoffice.meta }}</h3>
              <ul>
                <li v-for="f in q.homeoffice.faixas" :key="f.texto">
                  {{ f.texto }} → <strong>{{ f.percentual }}%</strong>
                </li>
              </ul>
            </div>
            <div class="mm-regra" :class="{ destaque: modalidade === 'executiva' }">
              <h3>Regional Executiva — meta {{ q.executiva.meta }}</h3>
              <ul>
                <li v-for="f in q.executiva.faixas" :key="f.texto">
                  {{ f.texto }} → <strong>{{ f.percentual }}%</strong>
                </li>
              </ul>
            </div>
          </div>
          <p v-if="q.soma" class="mm-nota">
            <i class="bx bx-info-circle" aria-hidden="true"></i>
            <span>{{ q.soma }}</span>
          </p>
        </div>
      </section>

      <!-- 2. Regra do 1º boleto -->
      <section v-else-if="passoAtual.key === 'boleto'">
        <p class="mm-lead">
          A adesão só vira meta quando o <strong>1º boleto é pago</strong> — e dentro de dois prazos:
          até <strong>30 dias depois do vencimento</strong> e até o <strong>dia 5 do mês do fechamento</strong>.
        </p>
        <ol class="mm-timeline" aria-label="Exemplo">
          <li><span class="mm-tl-data">24/09</span><span>Adesão feita</span></li>
          <li><span class="mm-tl-data">24/10</span><span>Vence o 1º boleto</span></li>
          <li class="ok"><span class="mm-tl-data">até 05/11</span><span>Pago aqui: conta no <strong>fechamento de novembro</strong></span></li>
          <li class="warn"><span class="mm-tl-data">06/11 a 23/11</span><span>Pago aqui: conta no <strong>fechamento de dezembro</strong></span></li>
          <li class="bad"><span class="mm-tl-data">depois de 23/11</span><span>Passou de 30 dias do vencimento: <strong>não conta como meta</strong></span></li>
        </ol>
        <p class="mm-nota">
          <i class="bx bx-calendar-exclamation" aria-hidden="true"></i>
          Se o dia 5 cair em fim de semana ou feriado, o pagamento só compensa no próximo dia útil —
          o painel avisa você quando isso acontecer.
        </p>
      </section>

      <!-- 3. Quantidade -->
      <section v-else-if="passoAtual.key === 'quantidade'">
        <p v-if="modoRevisao" class="mm-lead">
          Começou <strong>{{ mesRevisao }}</strong>! Revise quantas adesões você quer trazer neste mês.
          Já deixamos uma sugestão preenchida — ajuste se quiser.
        </p>
        <p v-else class="mm-lead">
          Já deixamos uma <strong>sugestão</strong> preenchida. A meta com folga cobre quem atrasa
          o 1º boleto — <strong>não é garantia</strong> de bater a meta.
        </p>
        <div v-for="p in produtosPlano" :key="p" class="mm-qtd">
          <label :for="'mm-qtd-' + p" class="mm-qtd-label">{{ regras[p].nome }} — adesões por mês</label>
          <div class="mm-qtd-row">
            <button type="button" class="btn btn-light mm-qtd-btn" aria-label="Diminuir" @click="alterarQtd(p, -1)">
              <i class="bx bx-minus" aria-hidden="true"></i>
            </button>
            <input
              :id="'mm-qtd-' + p"
              v-model.number="form[p === 'brasil' ? 'meta_brasil' : 'meta_truck']"
              type="number"
              inputmode="numeric"
              min="0"
              :max="metaMax"
              step="1"
              class="form-control mm-qtd-input"
              :aria-describedby="'mm-qtd-ajuda-' + p"
              @input="onInputQtd(p)"
            />
            <button type="button" class="btn btn-light mm-qtd-btn" aria-label="Aumentar" @click="alterarQtd(p, 1)">
              <i class="bx bx-plus" aria-hidden="true"></i>
            </button>
          </div>
          <p :id="'mm-qtd-ajuda-' + p" class="mm-qtd-ajuda">
            Meta: <strong>{{ regras[p].meta_oficial }}</strong> · Recomendado: <strong>{{ regras[p].recomendado }}</strong>
            <button type="button" class="btn btn-link btn-sm p-0 ml-1 align-baseline" @click="usarSugestao(p)">usar sugestão</button>
            <template v-if="p === 'truck' && temBrasil"><br />Não vai produzir Truck? Deixe <strong>0</strong>.</template>
          </p>
          <p v-if="mediaProduto(p)" class="mm-qtd-media">
            <i class="bx bx-line-chart" aria-hidden="true"></i>
            <span v-if="mediaVence(p)">
              Sugestão pela sua média: <strong>{{ fmtMedia(mediaProduto(p).media) }}</strong>
              {{ plural(mediaProduto(p).media, "adesão", "adesões") }}/mês nos últimos {{ mediaProduto(p).meses }} meses
              + {{ mediaProduto(p).acrescimo_pct }}% = <strong>{{ sugestaoProduto(p) }}</strong>.
            </span>
            <span v-else>
              Sua média nos últimos {{ mediaProduto(p).meses }} meses foi de
              <strong>{{ fmtMedia(mediaProduto(p).media) }}</strong> {{ plural(mediaProduto(p).media, "adesão", "adesões") }}/mês
              — mantivemos a sua meta atual de <strong>{{ metaCadastrada(p) }}</strong>.
            </span>
          </p>
          <p v-else-if="semMedia(p)" class="mm-qtd-media">
            <i class="bx bx-line-chart" aria-hidden="true"></i>
            <span>
              Sem adesões nos últimos {{ config.sugestao_media[p].meses }} meses para calcular a média —
              mantivemos a sua meta atual de <strong>{{ metaCadastrada(p) }}</strong>.
            </span>
          </p>
          <div v-if="!quantidadeValida(form[p === 'brasil' ? 'meta_brasil' : 'meta_truck'])" class="mm-aviso danger" role="alert">
            Informe um número inteiro de 0 a {{ metaMax }}.
          </div>
          <div v-else-if="avisos[p]" class="mm-aviso" :class="avisos[p].nivel" role="status">
            {{ avisos[p].texto }}
          </div>
        </div>
      </section>

      <!-- 4. Prévia -->
      <section v-else-if="passoAtual.key === 'previa'">
        <div class="mm-previa">
          <div v-for="l in previa.linhas" :key="l.produto" class="mm-previa-linha">
            <span>{{ l.nome }}: {{ l.qtd }} {{ plural(l.qtd, "adesão", "adesões") }} × {{ fmtMoeda(l.base) }}</span>
            <strong>{{ fmtMoeda(l.valor) }}</strong>
          </div>
          <div class="mm-previa-total">
            <span>Faturamento estimado das suas adesões / mês</span>
            <strong>{{ fmtMoeda(previa.total) }}</strong>
          </div>
          <div class="mm-previa-sub">Em 12 meses: {{ fmtMoeda(previa.anual) }}</div>
        </div>
        <p class="mm-nota">
          <i class="bx bx-calculator" aria-hidden="true"></i>
          <span>
            <strong>Premissa:</strong>
            <template v-for="(l, i) in previa.linhas">
              <template v-if="i > 0"> </template>
              <span :key="'premissa-' + l.produto">
                {{ l.nome }}: adesão mínima de {{ fmtMoeda(premissas[l.produto].adesao_minima) }}, dos quais
                {{ fmtMoeda(premissas[l.produto].rastreador) }}
                (média) vão para o rastreador — base de
                {{ fmtMoeda(premissas[l.produto].base_adesao) }} por adesão.
              </span>
            </template>
            É uma <strong>estimativa</strong> do valor das adesões que você traz; não é o cálculo da sua comissão
            (a recorrente de 5%/7%/10% depende dos boletos pagos da base).
          </span>
        </p>
        <p v-if="previa.linhas.length" class="mm-aviso warning" role="note">
          <i class="bx bx-error" aria-hidden="true"></i>
          O valor do rastreador pode variar de {{ fmtMoeda(RASTREADOR_FAIXA[0]) }} até {{ fmtMoeda(RASTREADOR_FAIXA[1]) }}.
          A prévia usa o desconto médio de
          {{ previa.linhas.map((l) => fmtMoeda(premissas[l.produto].rastreador) + " no " + l.nome).join(" e ") }},
          então o valor real das suas adesões pode ser um pouco maior ou menor.
        </p>
        <div v-if="erro" class="mm-aviso danger" role="alert">{{ erro }}</div>
      </section>

      <!-- Pronto -->
      <section v-else-if="passoAtual.key === 'pronto'" class="mm-pronto">
        <div class="mm-pronto-icone" aria-hidden="true"><i class="bx bx-trophy"></i></div>
        <p class="mm-lead text-center">Sua trajetória para este mês:</p>
        <div v-for="l in previa.linhas" :key="l.produto" class="mm-trajetoria">
          <h3>{{ l.nome }}</h3>
          <div class="mm-trajetoria-grid">
            <div><span>Meta mensal</span><strong>{{ l.qtd }}</strong></div>
            <div><span>Por semana</span><strong>≈ {{ porSemana(l.qtd) }}</strong></div>
            <div v-if="aCadaDias(l.qtd)"><span>Ritmo</span><strong>1 a cada {{ aCadaDias(l.qtd) }} {{ plural(aCadaDias(l.qtd), "dia", "dias") }}</strong></div>
          </div>
        </div>
        <p class="mm-nota">
          <i class="bx bx-bar-chart-alt-2" aria-hidden="true"></i>
          Em <strong>Minhas Metas</strong> você acompanha o progresso, as placas que ainda não pagaram o 1º boleto
          e o prazo de cada uma. Dá para refazer este planejamento quando quiser.
        </p>
      </section>
    </div>

    <div v-else class="text-center py-4" role="status">
      <b-spinner small class="mr-2" /> Carregando...
    </div>

    <template #modal-footer>
      <div class="mm-footer">
        <template v-if="salvo">
          <button type="button" class="btn btn-light" @click="fechar">Fechar</button>
          <button type="button" class="btn btn-default" @click="verPainel">Ver Minhas Metas</button>
        </template>
        <template v-else>
          <button v-if="passo === 0" type="button" class="btn btn-light" @click="fechar">Agora não</button>
          <button v-else type="button" class="btn btn-light" :disabled="salvando" @click="voltar">Voltar</button>
          <button type="button" class="btn btn-default" :disabled="!podeAvancar || salvando || !config" @click="avancar">
            <b-spinner v-if="salvando" small class="mr-1" />
            {{ ultimoPasso ? "Salvar planejamento" : "Continuar" }}
          </button>
        </template>
      </div>
    </template>
  </b-modal>
</template>

<style scoped>
.mm-wizard-kicker {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--st-text-muted);
}
.mm-wizard-title {
  display: block;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--st-brand);
}
.mm-steps {
  display: flex;
  gap: 0.5rem;
  margin: 0 0 1rem;
  padding: 0;
  list-style: none;
}
.mm-steps li {
  flex: 1;
}
.mm-steps button {
  width: 100%;
  min-height: 2rem;
  border: 0;
  border-radius: 999px;
  background: var(--st-canvas);
  color: var(--st-text-muted);
  font-size: 0.8125rem;
  font-weight: 600;
}
.mm-steps li.feito button {
  background: var(--st-brand-soft);
  color: var(--st-brand);
  cursor: pointer;
}
.mm-steps li.ativo button {
  background: var(--st-brand);
  color: var(--st-text-inverse);
}
.mm-lead {
  font-size: 0.9375rem;
  color: var(--st-text);
}
.mm-nota {
  display: flex;
  gap: 0.5rem;
  margin: 1rem 0 0;
  padding: 0.625rem 0.75rem;
  border-radius: var(--st-radius);
  background: var(--st-brand-soft);
  font-size: 0.875rem;
  color: var(--st-text);
}
.mm-nota i {
  font-size: 1.125rem;
  color: var(--st-brand);
}
.mm-regra-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}
.mm-regra-grid-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.mm-produto-regras + .mm-produto-regras {
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid var(--st-border-soft);
}
.mm-produto-titulo {
  margin: 0 0 0.625rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--st-brand);
}
@media (max-width: 767.98px) {
  .mm-regra-grid-3 {
    grid-template-columns: minmax(0, 1fr);
  }
}
.mm-regra {
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--st-border-soft);
  border-radius: var(--st-radius);
}
.mm-regra.destaque {
  border-color: var(--st-brand);
  box-shadow: 0 0 0 1px var(--st-brand) inset;
}
.mm-regra h3 {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--st-brand);
}
.mm-regra ul {
  margin: 0;
  padding-left: 1.1rem;
  font-size: 0.875rem;
}
.mm-regra li + li {
  margin-top: 0.375rem;
}
.mm-timeline {
  margin: 0;
  padding: 0;
  list-style: none;
  border-left: 3px solid var(--st-border);
}
.mm-timeline li {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  padding: 0.375rem 0 0.375rem 1rem;
  font-size: 0.875rem;
}
.mm-timeline li::before {
  content: "";
  position: absolute;
  left: -0.45rem;
  top: 0.7rem;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  background: var(--st-border);
}
.mm-timeline li.ok::before { background: var(--st-success); }
.mm-timeline li.warn::before { background: var(--st-warning); }
.mm-timeline li.bad::before { background: var(--st-danger); }
.mm-tl-data {
  min-width: 8rem;
  font-weight: 700;
  color: var(--st-text);
}
.mm-fieldset {
  margin-bottom: 1rem;
}
.mm-fieldset legend {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--st-text);
}
.mm-sim-nao {
  display: flex;
  gap: 0.5rem;
}
.mm-sim-nao label {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 2.75rem;
  margin: 0;
  padding: 0 0.875rem;
  border: 1px solid var(--st-border);
  border-radius: var(--st-radius);
  cursor: pointer;
}
.mm-sim-nao label.sel,
.mm-objetivo.sel {
  border-color: var(--st-brand);
  background: var(--st-brand-soft);
}
.mm-objetivos {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}
.mm-objetivo {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
  padding: 0.75rem;
  border: 1px solid var(--st-border);
  border-radius: var(--st-radius);
  cursor: pointer;
}
.mm-objetivo:focus-within {
  outline: 2px solid var(--st-focus-ring);
  outline-offset: 2px;
}
.mm-objetivo i {
  font-size: 1.5rem;
  color: var(--st-brand);
}
.mm-objetivo-titulo {
  font-weight: 700;
  color: var(--st-text);
}
.mm-objetivo-texto {
  font-size: 0.8125rem;
  color: var(--st-text-muted);
}
.mm-qtd + .mm-qtd {
  margin-top: 1.25rem;
}
.mm-qtd-label {
  font-weight: 600;
}
.mm-qtd-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 16rem;
}
.mm-qtd-btn {
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  padding: 0;
  font-size: 1.25rem;
}
.mm-qtd-input {
  height: 2.75rem;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
}
.mm-qtd-ajuda {
  margin: 0.375rem 0 0;
  font-size: 0.8125rem;
  color: var(--st-text-muted);
}
.mm-qtd-media {
  display: flex;
  gap: 0.375rem;
  margin: 0.5rem 0 0;
  padding: 0.5rem 0.75rem;
  border-radius: var(--st-radius);
  background: var(--st-brand-soft);
  font-size: 0.8125rem;
  color: var(--st-text);
}
.mm-qtd-media i {
  font-size: 1rem;
  color: var(--st-brand);
}
.mm-aviso {
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: var(--st-radius);
  font-size: 0.875rem;
}
.mm-aviso.danger { background: var(--st-danger-soft); color: var(--st-danger-strong); }
.mm-aviso.warning { background: var(--st-warning-soft); color: var(--st-warning-strong); }
.mm-previa {
  padding: 0.875rem 1rem;
  border: 1px solid var(--st-border-soft);
  border-radius: var(--st-radius-lg);
}
.mm-previa-linha,
.mm-previa-total {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.25rem 0;
  font-size: 0.9375rem;
}
.mm-previa-total {
  margin-top: 0.375rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--st-border-soft);
  font-size: 1rem;
}
.mm-previa-total strong {
  font-size: 1.25rem;
  color: var(--st-success-strong);
}
.mm-previa-sub {
  text-align: right;
  font-size: 0.8125rem;
  color: var(--st-text-muted);
}
.mm-pronto-icone {
  text-align: center;
  font-size: 3rem;
  color: var(--st-warning);
}
.mm-trajetoria + .mm-trajetoria {
  margin-top: 0.75rem;
}
.mm-trajetoria h3 {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--st-brand);
}
.mm-trajetoria-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}
.mm-trajetoria-grid div {
  padding: 0.5rem 0.625rem;
  border-radius: var(--st-radius);
  background: var(--st-canvas);
}
.mm-trajetoria-grid span {
  display: block;
  font-size: 0.75rem;
  color: var(--st-text-muted);
}
.mm-trajetoria-grid strong {
  font-size: 1.0625rem;
  color: var(--st-text);
}
.mm-footer {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
}
.mm-footer .btn {
  min-height: 2.75rem;
  min-width: 7rem;
}
@media (max-width: 575.98px) {
  .mm-regra-grid,
  .mm-objetivos,
  .mm-trajetoria-grid {
    grid-template-columns: 1fr;
  }
  .mm-qtd-row {
    max-width: none;
  }
  .mm-footer .btn {
    flex: 1;
    min-width: 0;
  }
}
</style>

<style>
/* Não-scoped: o b-modal renderiza o dialog fora da árvore deste componente. */
@media (max-width: 575.98px) {
  .mm-wizard-dialog {
    margin: 0;
    max-width: none;
    min-height: 100%;
  }
  .mm-wizard-dialog .mm-wizard-content {
    min-height: 100vh;
    min-height: 100dvh;
    border: 0;
    border-radius: 0;
  }
  .mm-wizard-dialog .modal-footer {
    padding-bottom: calc(0.75rem + env(safe-area-inset-bottom));
  }
}
</style>
